// ── Đơn vị trả công trên màn Cơ chế lương: THẠCH LƯƠNG THẢO ─────────────
//
// CEO chốt 14a 16/09/2026: ô trả theo việc hiện "3 thạch lương thảo", dưới là
// dòng mờ "= 30.000đ".
//
// Sổ, cửa API và máy tính lương VẪN là đồng. Màn chỉ HIỆN và NHẬN bằng thạch
// lương thảo, nên hai hàm quy đổi ở đây phải đi trọn vòng không lệch một đồng:
// hiện ra số nào, gõ lại đúng số đó là ra lại đúng số đồng cũ. KHÔNG làm tròn
// ở bất cứ đâu — làm tròn để hiện là mở màn ra rồi bấm Lưu thì lương người ta
// đổi mà không ai gõ gì.

// Chép tay từ payroll.DefaultPointRateVND bên Go. Bài
// __tests__/pay-unit-leaves.golden.test.ts so con số này với đơn giá máy ghi
// vào tệp chuẩn — lệch là đỏ.
export const POINT_RATE_VND = 10_000;

// Luôn viết đủ ba chữ (CEO chốt 11b).
export const PAY_UNIT_NAME = "thạch lương thảo";
// Cùng tên, viết hoa chữ đầu — dùng khi đứng đầu nhãn hay đầu dòng tóm tắt.
export const PAY_UNIT_TITLE = "Thạch lương thảo";

// Đơn giá là lũy thừa của 10 nên 1đ đúng bằng 0,0001 thạch lương thảo: mọi
// mức đồng nguyên đều ra số thập phân dừng, tối đa chừng này chữ số.
const FRACTION_DIGITS = String(POINT_RATE_VND).length - 1;
const VND_PER_LAST_DIGIT = POINT_RATE_VND / 10 ** FRACTION_DIGITS;

// Sổ ghi phần trăm thạch lương thảo, nên ô chỉ nhận tối đa hai chữ số thập phân.
export const PAY_UNIT_INPUT_DECIMALS = 2;

const vndFormatter = new Intl.NumberFormat("vi-VN");

// Đồng → chữ thạch lương thảo: dấu phẩy thập phân, KHÔNG chia nhóm nghìn —
// "1.000" người Việt đọc là một nghìn, còn ô nhập đọc là 1.
export function formatPayUnits(vnd: number): string {
  if (!Number.isSafeInteger(vnd)) {
    return String(vnd / POINT_RATE_VND).replace(".", ",");
  }
  const sign = vnd < 0 ? "-" : "";
  const abs = Math.abs(vnd);
  const whole = Math.trunc(abs / POINT_RATE_VND);
  const rest = abs % POINT_RATE_VND;
  if (rest === 0) return `${sign}${whole}`;
  const fraction = String(rest).padStart(FRACTION_DIGITS, "0").replace(/0+$/, "");
  return `${sign}${whole},${fraction}`;
}

// Chữ thạch lương thảo → đồng; null khi chữ chưa phải một mức sổ ghi được.
// Chỉ tính bằng số nguyên: 2,55 × 10.000 bằng số thực là 25499,999…, hụt 1đ.
//
// maxDecimals chỉ để ô nhập cho xoá lùi một mức cũ lẻ hơn hai chữ số. Trần vẫn
// là một đồng: chữ số lẻ thứ năm là phần lẻ của đồng, sổ không có.
export function parsePayUnits(
  raw: string,
  opts?: { signed?: boolean; maxDecimals?: number },
): number | null {
  const match = /^(-)?(\d*)(?:\.(\d*))?$/.exec(raw.trim().replace(",", "."));
  if (!match) return null;
  const [, minus, whole = "", fraction = ""] = match;
  if (minus && !opts?.signed) return null;
  if (whole === "" && fraction === "") return null;
  const maxDecimals = Math.min(opts?.maxDecimals ?? PAY_UNIT_INPUT_DECIMALS, FRACTION_DIGITS);
  if (fraction.length > maxDecimals) return null;
  const abs =
    Number(whole || "0") * POINT_RATE_VND +
    Number(fraction.padEnd(FRACTION_DIGITS, "0")) * VND_PER_LAST_DIGIT;
  if (!Number.isSafeInteger(abs)) return null;
  if (abs === 0) return 0;
  return minus ? -abs : abs;
}

// Dòng mờ "= 30.000đ" dưới ô.
export function formatVnd(vnd: number): string {
  const sign = vnd < 0 ? "-" : "";
  return `${sign}${vndFormatter.format(Math.abs(vnd))}đ`;
}

// Khoản cộng/bớt riêng một người: số dương cũng mang dấu, vì "5.000đ" đứng
// một mình đọc thành một mức giá chứ không phải "thêm 5.000đ".
export function formatSignedVnd(vnd: number): string {
  return vnd > 0 ? `+${formatVnd(vnd)}` : formatVnd(vnd);
}

// Một dòng trong bảng điều chỉnh: "+0,5 thạch lương thảo (= +5.000đ)".
export function formatPayUnitDelta(vnd: number): string {
  const sign = vnd > 0 ? "+" : "";
  return `${sign}${formatPayUnits(vnd)} ${PAY_UNIT_NAME} (= ${formatSignedVnd(vnd)})`;
}

// Đơn giá trên màn chi tiết lương của từng người (CEO chốt 15b 16/09/2026):
// "3 thạch lương thảo (= 30.000đ)". Một mức giá nên không mang dấu.
//
// Sau "=" là dấu cách KHÔNG NGẮT: trên điện thoại dòng này hay phải xuống
// dòng, dấu cách thường để "(=" treo cuối dòng còn số đồng rơi xuống dưới.
export function formatPayUnitPrice(vnd: number): string {
  return `${formatPayUnits(vnd)} ${PAY_UNIT_NAME} (=\u00a0${formatVnd(vnd)})`;
}

const NOWRAP_NAME = PAY_UNIT_NAME.replace(/ /g, "\u00a0");
const NOWRAP_TITLE = PAY_UNIT_TITLE.replace(/ /g, "\u00a0");

// Dán ba chữ tên đơn vị lại bằng dấu cách KHÔNG NGẮT, ngay lúc VẼ ra màn.
//
// Tên đơn vị dài ba chữ nên trong cột hẹp của điện thoại nó hay bị ngắt giữa
// chừng: "thạch" nằm cuối dòng, "lương thảo" rơi xuống dòng dưới. Người đọc
// mất một nhịp mới nhận ra đó vẫn là MỘT đơn vị, và dòng phép tính trên phiếu
// lương thì đọc xuôi mới hiểu (CEO chốt câu 4a ngày 23/09/2026).
//
// CỐ Ý dán lúc vẽ chứ không dán vào chuỗi gốc: chuỗi gốc còn đi vào bài kiểm,
// vào chỗ chép ra, vào chỗ tìm kiếm — nhét ký tự lạ vào đó là đổi thứ người
// khác đang so, để sửa một chuyện chỉ thuộc về cách trình bày.
export function keepPayUnitTogether(text: string): string {
  return text.split(PAY_UNIT_TITLE).join(NOWRAP_TITLE).split(PAY_UNIT_NAME).join(NOWRAP_NAME);
}
